'use client';

import React, { useState, useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { CaptchaDialog as Dialog, CaptchaDialogContent as DialogContent } from '@/components/SmartCaptchaDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Phone, CheckCircle2, AlertCircle } from 'lucide-react';
import { useLanguage } from '@/lib/config/useLanguage';
import { getBranchData, getBranchCodeFromPath } from '@/lib/config/branches-config';
import {
  buildLeadThankYouUrl,
  clearPendingLeadSubmissionId,
  extractOptionalLeadId,
  getOrCreatePendingLeadSubmissionId,
} from '@/lib/forms/lead-thank-you';
import { reachMetrikaGoal, trackLeadFormStage, trackLeadFormSuccess } from '@/lib/analytics/metrika';
import { useSmartCaptcha } from '@/components/SmartCaptcha';

import { useLeadFormAnalytics } from '@/lib/analytics/use-lead-form-analytics';
import { formatRussianPhone, RUSSIAN_PHONE_PATTERN } from '@/lib/forms/contact-validation';

const FORM_TYPE = 'product-price-request';
const PHONE_CLICK_GOAL = 'site-phone-click-product';
const PRICE_BUTTON_CLICK_GOAL = 'site-button-click-product-price';

interface ProductPriceActionsProps {
  productName?: string;
  productCode?: string;
  currentPrice?: number;
  oldPrice?: number;
}

export default function ProductPriceActions({ productName, productCode }: ProductPriceActionsProps) {
  const pathname = usePathname() || '/';
  const router = useRouter();
  const analytics = useLeadFormAnalytics(FORM_TYPE, false);
  const { localizePath } = useLanguage();
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    privacy: false,
    marketing: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const { captchaElement, executeCaptcha, resetCaptcha, isCaptchaReady, challengeVisible } = useSmartCaptcha();

  // Получаем данные текущего филиала для телефона на основе пути
  const currentBranch = useMemo(() => {
    const branchCode = getBranchCodeFromPath(pathname);
    return getBranchData(branchCode);
  }, [pathname]);
  
  const phoneNumber = currentBranch?.phone || '+7 (812) 333-33-33';

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'phone' ? formatRussianPhone(value) : value }));
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handlePriceButtonClick = () => {
    reachMetrikaGoal(PRICE_BUTTON_CLICK_GOAL);
    trackLeadFormStage(FORM_TYPE, 'open');
    setIsPriceModalOpen(true);
  };

  const handlePhoneButtonClick = () => {
    reachMetrikaGoal(PHONE_CLICK_GOAL);
    window.location.href = `tel:${phoneNumber.replace(/\s/g, '')}`;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Проверка обязательных полей
    if (!formData.privacy) {
      setSubmitStatus('error');
      setSubmitMessage('Необходимо согласие на обработку персональных данных');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitMessage('');

    try {
      const submissionId = getOrCreatePendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
      const smartToken = await executeCaptcha();
      // Формируем сообщение с информацией о товаре
      let message = formData.message;
      if (productName) {
        const productInfo = `Товар: ${productName}${productCode ? ` (Артикул: ${productCode})` : ''}`;
        message = message ? `${productInfo}\n\n${message}` : productInfo;
      }

      const response = await fetch('/api/forms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: message,
          privacy: formData.privacy,
          marketing: formData.marketing,
          formType: FORM_TYPE,
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
          productName: productName || '',
          productCode: productCode || '',
          smartToken,
          submissionId,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        trackLeadFormSuccess(FORM_TYPE, submissionId);

        const thankYouUrl = buildLeadThankYouUrl({
          pathname: window.location.pathname,
          search: window.location.search,
          formType: FORM_TYPE,
          leadId: extractOptionalLeadId(data),
          sid: submissionId,
        });

        clearPendingLeadSubmissionId(FORM_TYPE, window.location.pathname);
        router.push(thankYouUrl);
        return;
      } else {
        trackLeadFormStage(FORM_TYPE, 'error', 'server');
        setSubmitStatus('error');
        setSubmitMessage(data.message || 'Ошибка при отправке заявки');
      }
    } catch (error) {
      trackLeadFormStage(FORM_TYPE, 'error', 'network-or-captcha');
      setSubmitStatus('error');
      setSubmitMessage('Произошла ошибка. Попробуйте позже.');
      console.error('Ошибка при отправке формы:', error);
    } finally {
      resetCaptcha();
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Card className="w-full border border-slate-200 shadow">
        <CardHeader className="px-4 py-4 sm:p-6">
          <CardTitle className="text-base leading-snug sm:text-lg">
            Нужна стоимость под ваши размеры и задачу?
          </CardTitle>
          <CardDescription className="text-sm">
            Уточним комплектацию, изготовление, доставку и монтаж.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 px-4 pb-4 sm:space-y-4 sm:px-6 sm:pb-6">
          <div className="flex flex-row gap-2 sm:gap-3">
            <Button
              type="button"
              variant="default"
              size="default"
              onClick={handlePriceButtonClick}
              className="flex-1 whitespace-nowrap bg-slate-900 px-3 text-xs sm:px-4 sm:text-sm text-slate-50 hover:bg-slate-900/90"
            >
              УЗНАТЬ ЦЕНУ
            </Button>

            <Button
              type="button"
              variant="outline"
              size="default"
              onClick={handlePhoneButtonClick}
              className="flex-1 whitespace-nowrap px-3 text-xs sm:px-4 sm:text-sm"
            >
              <Phone className="h-4 w-4" />
              ПОЗВОНИТЬ
            </Button>
          </div>

          <p className="text-xs leading-snug text-slate-500 text-center dark:text-slate-400">
            Свяжитесь с нами для бесплатной консультации и расчёта стоимости
          </p>
        </CardContent>
      </Card>

      {/* Модальное окно запроса цены */}
      <Dialog challengeVisible={challengeVisible} busy={isSubmitting} open={isPriceModalOpen} onOpenChange={setIsPriceModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{productName ? `Рассчитать: ${productName}` : 'Запрос расчёта стоимости'}</DialogTitle>
            <DialogDescription>
              Оставьте телефон. Уточним размеры и комплектацию и подготовим расчёт стоимости.
            </DialogDescription>
          </DialogHeader>
          <form {...analytics} onSubmit={handleFormSubmit} className="mt-4 space-y-4">
            {productName && (
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600">Товар:</p>
                <p className="font-medium">{productName}</p>
              </div>
            )}
            <div>
              <Label htmlFor="price-name" className="text-sm font-medium text-gray-700">
                Имя (необязательно)
              </Label>
              <Input
                id="price-name"
                name="name"
                type="text"
                autoComplete="name"
                value={formData.name}
                onChange={handleFormChange}
                className="mt-2"
                placeholder="Иванов Иван Иванович"
              />
            </div>
            <div>
              <Label htmlFor="price-phone" className="text-sm font-medium text-gray-700">
                Телефон <span className="text-red-500">*</span>
              </Label>
              <Input
                id="price-phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                pattern={RUSSIAN_PHONE_PATTERN}
                required
                value={formData.phone}
                onChange={handleFormChange}
                className="mt-2"
                placeholder="+7 (999) 123-45-67"
              />
            </div>

            <div>
              <Label htmlFor="price-email" className="text-sm font-medium text-gray-700">
                Email для расчёта (необязательно)
              </Label>
              <Input
                id="price-email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleFormChange}
                className="mt-2"
                placeholder="example@mail.com"
              />
            </div>

            <div>
              <Label htmlFor="price-message" className="text-sm font-medium text-gray-700">
                Дополнительная информация
              </Label>
              <textarea
                id="price-message"
                name="message"
                value={formData.message}
                onChange={handleFormChange}
                className="mt-2 flex min-h-[100px] w-full rounded-md border border-slate-200 bg-transparent px-3 py-2 text-base shadow-sm transition-colors placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:border-slate-800 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300"
                placeholder="Укажите размеры, количество, город доставки и нужен ли монтаж"
                rows={4}
              />
            </div>

            {/* Чекбоксы согласия */}
            <div className="space-y-3">
              <div className="flex items-start">
                <Checkbox
                  id="price-privacy"
                  checked={formData.privacy}
                  onCheckedChange={(checked) => handleCheckboxChange('privacy', checked as boolean)}
                  required
                  className="mt-1"
                />
                <Label htmlFor="price-privacy" className="ml-2 text-sm text-gray-600">
                  Я даю согласие на обработку своих персональных данных и соглашаюсь с{' '}
                  <a href={localizePath('/pages/politika-konfidentsialnosti-personalnykh-dannykh')} className="text-blue-600 hover:underline">
                    политикой конфиденциальности
                  </a>
                  . <span className="text-red-500">*</span>
                </Label>
              </div>

              <div className="flex items-start">
                <Checkbox
                  id="price-marketing"
                  checked={formData.marketing}
                  onCheckedChange={(checked) => handleCheckboxChange('marketing', checked as boolean)}
                  className="mt-1"
                />
                <Label htmlFor="price-marketing" className="ml-2 text-sm text-gray-600">
                  Я даю согласие на направление рекламных рассылок.
                </Label>
              </div>
            </div>

            {/* Сообщения о результате */}
            {submitStatus === 'success' && (
              <div className="flex items-center gap-2 rounded-lg bg-green-50 p-4 text-green-800">
                <CheckCircle2 className="h-5 w-5 shrink-0" />
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-800">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <p className="text-sm font-medium">{submitMessage}</p>
              </div>
            )}

            {captchaElement}

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsPriceModalOpen(false);
                  setSubmitStatus('idle');
                  setSubmitMessage('');
                }}
                disabled={isSubmitting}
              >
                Отмена
              </Button>
              <Button
                type="submit"
                variant="default"
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                disabled={!isCaptchaReady || isSubmitting || !formData.privacy}
              >
                {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
